'use client';
import { faCalendar, faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import moment from 'moment';
import { usePathname, useSearchParams } from 'next/navigation';

import HeaderGameTimeBox from '@/modules/league/new-components/header/HeaderGameTimeBox';
import { GameDayInfo } from '@/modules/league/new-components/types';
import { formatDateWithTimezone } from '@/utils/actions/leagues/gameDay/functions/getHeaderGameTimeBox';

interface GameDayHeaderProps {
  headerContent: GameDayInfo;
}

const HeaderContent = ({ headerContent }: GameDayHeaderProps) => {
  const { details } = headerContent;

  const pathname = usePathname();
  const searchParams = useSearchParams();

  const pageType = searchParams?.get('tab');
  const isRsvpPage = pageType === 'rsvp';
  const isGroupsPage = pageType === 'groups';
  const isMatchesPage = pageType === 'matches';

  const formattedDate = moment.parseZone(details?.FromDateTime).format('MMM DD, YYYY, h:mm A');
  const timezoneAbbreviation = formatDateWithTimezone(
    details?.FromDateTime,
    details?.Timezone_IanaID
  );

  return (
    <div className='mx-auto h-auto w-full gap-8 md:w-full lg:w-[860px]'>
      <div className='flex w-full flex-col items-start px-3 py-2 md:w-full md:items-center'>
        <h1 className='w-full text-left text-lg font-bold leading-6 text-leagues-300 md:text-center md:text-2xl lg:text-4xl '>
          {details.leagueTitle}
        </h1>
        <div className='mt-2 flex flex-col gap-2 text-gray-900 md:flex-row md:justify-center md:gap-4 md:font-semibold'>
          <div className='flex items-center'>
            <span className='mr-2 text-sm md:mr-4'>Session: {details.sessionNumber}</span>
            <span className='inline-block font-extralight'>|</span>
            <div className='flex items-center text-sm font-normal md:hidden'>
              <FontAwesomeIcon className='mx-2 md:mr-2' icon={faCalendar} size='lg' />
              {formattedDate} {timezoneAbbreviation}
            </div>
          </div>
          <div
            className={`items-center text-sm font-medium ${isRsvpPage ? 'flex' : 'hidden md:flex'}`}
          >
            <FontAwesomeIcon className='mr-2 md:mr-4' icon={faLocationDot} size='lg' />
            {details.locationTitle}
          </div>
          <span className='hidden font-extralight md:inline-block'>|</span>
          <div className='hidden items-center text-sm font-normal md:flex'>
            <FontAwesomeIcon className='mr-4' icon={faCalendar} size='lg' />
            {formattedDate} {timezoneAbbreviation}
          </div>
        </div>
        <div className='mt-3 hidden border-t-[0.5px] border-gray-900 md:block md:w-[597px]' />

        <HeaderGameTimeBox
          gameDayInfo={details}
          isFlexLeague={details.isFlexLeague}
          isTraditionalLeague={details.traditionalLeague}
          isRsvpPage={isRsvpPage}
        />

        {((details.isLocked && isGroupsPage) || (isMatchesPage && details.isMatchStarted)) &&
        details.UserIsInGroup ? (
          <CustomLink
            href={`${pathname}?tab=${pageType}&${isGroupsPage ? 'myGroup' : 'matchGroup'}=${details.CurrentUser_PoolID}&myGroup=true`}
            className='mt-2 hidden cursor-pointer text-center text-sm font-medium text-leagues-500 hover:text-leagues-500/90 md:flex'
          >{`View Your Group: ${details.PoolNumber}`}</CustomLink>
        ) : null}
      </div>
      {((details.isLocked && isGroupsPage) || (isMatchesPage && details.isMatchStarted)) &&
      details.UserIsInGroup ? (
        <CustomLink
          href={`${pathname}?tab=${pageType}&${isGroupsPage ? 'myGroup' : 'matchGroup'}=${details.CurrentUser_PoolID}&myGroup=true`}
        >
          <div className='cursor-pointer bg-leagues-500 py-1.5 text-center text-sm font-medium text-leagues-300/10 md:hidden'>
            {`View Your Group: ${details.PoolNumber}`}
          </div>
        </CustomLink>
      ) : null}
    </div>
  );
};

export default HeaderContent;
